import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, MessageSquare, Mic, Sparkles, Check, Building2, Stethoscope, ShoppingBag, GraduationCap, Scale, UtensilsCrossed, Code2, MousePointerClick, Database, Zap, Copy, CalendarCheck, ClipboardList, Headphones, TrendingUp, Target, Package, BookOpen, Bot, Globe, FileText, Brain, RefreshCw, Users, Quote } from "lucide-react";
import Seo, { ORG_JSON_LD } from "@/components/marketing/Seo";
import { MarketingHeader, MarketingFooter } from "@/components/marketing/MarketingLayout";
import LandingChatBubble from "@/components/marketing/LandingChatBubble";
import VoiceCallingFlow from "@/components/marketing/VoiceCallingFlow";

const SITE_LD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  url: "https://hellosir.ai",
  name: "HelloSir.ai",
  publisher: ORG_JSON_LD,
  potentialAction: {
    "@type": "SearchAction",
    target: "https://hellosir.ai/use-cases?q={search_term_string}",
    "query-input": "required name=search_term_string",
  },
};

const HERO_BG = "https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwdGVjaG5vbG9neSUyMGFic3RyYWN0JTIwcGF0dGVybiUyMHdoaXRlfGVufDB8fHx8MTc4MTcyNjc2Nnww&ixlib=rb-4.1.0&q=85";
const VOICE_IMG = "https://images.unsplash.com/photo-1603714228681-b399854b8f80?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHw0fHxjdXN0b21lciUyMHNlcnZpY2UlMjBhZ2VudCUyMGhlYWRzZXR8ZW58MHx8fHwxNzgxNzI2Nzc2fDA&ixlib=rb-4.1.0&q=85";
const AVATAR_IMG = "https://images.unsplash.com/photo-1573496130141-209d200cebd8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBkaXZlcnNlJTIwYnVzaW5lc3MlMjBwb3J0cmFpdHN8ZW58MHx8fHwxNzgxNzI2NzY2fDA&ixlib=rb-4.1.0&q=85";

const Industries = [
  { id: "healthcare", name: "Healthcare", Icon: Stethoscope, href: "/use-cases/healthcare" },
  { id: "education", name: "Education", Icon: GraduationCap, href: "/use-cases/education" },
  { id: "real_estate", name: "Real Estate", Icon: Building2, href: "/use-cases/real-estate" },
  { id: "ecommerce", name: "eCommerce", Icon: ShoppingBag, href: "/use-cases/ecommerce" },
  { id: "law", name: "Law Firms", Icon: Scale, href: "/use-cases/law-firms" },
  { id: "restaurant", name: "Restaurants", Icon: UtensilsCrossed, href: "/use-cases/restaurants" },
];

const FreeTrial = {
  title: "Free starter — no card needed",
  price: 0,
  features: [
    "100 web chat conversations",
    "20 minutes of web voice agent",
    "1 AI agent",
    "1 website scrape (auto-train)",
    "Embeddable chat widget for your site",
  ],
};

const TopUps = [
  { id: "chat", name: "Chat Pack", price: 9.99, unit: "3,000 chats", icon: "💬", desc: "Best for support sites & FAQs. Above the pack: $0.003 per extra chat.", color: "#0044FF" },
  { id: "web_voice", name: "Web Voice Pack", price: 9.99, unit: "100 minutes", icon: "🎙", desc: "Browser-based voice agent. Above the pack: $0.10 per extra minute.", color: "#10B981", popular: true },
  { id: "phone", name: "AI Voice Calling", price: 9.99, unit: "25 minutes", icon: "📞", desc: "Inbound & outbound phone calls via VAPI. Above the pack: $0.40 per extra minute.", color: "#7C3AED" },
  { id: "scrape", name: "AI Agent", price: 1.00, unit: "1 agent + 1 site", icon: "🤖", desc: "Each additional agent works with one scraped website.", color: "#F97316" },
];

// Per-channel pricing primitives — drive both the cards and the calculator
const PACK = {
  chat:      { free: 10,  packQty: 3000, packPrice: 9.99 },  // → $0.00333 per extra chat
  webVoice:  { free: 10,  packQty: 100,  packPrice: 9.99 },  // → $0.0999  per extra min
  phone:     { free: 0,   packQty: 25,   packPrice: 9.99 },  // → $0.3996  per extra min
  scrape:    { free: 0,   packQty: 1,    packPrice: 1.00 },  // → linear
};

function packCost(usage, { free, packQty, packPrice }) {
  // 0 if within the free quota; otherwise minimum one pack ($9.99) plus per-unit overage.
  const need = Math.max(0, usage - free);
  if (need === 0) return { cost: 0, packs: 0, overage: 0, overageCost: 0 };
  if (need <= packQty) return { cost: packPrice, packs: 1, overage: 0, overageCost: 0 };
  const perUnit = packPrice / packQty;
  const overage = need - packQty;
  const overageCost = overage * perUnit;
  return { cost: packPrice + overageCost, packs: 1, overage, overageCost, perUnit };
}

function MultilingualVoiceDemo() {
  // Rotates through caller→agent exchanges in different languages, showing
  // the RAG (Knowledge Base → Agent) flow that powers each reply.
  const TURNS = [
    { lang: "English",  flag: "🇺🇸", caller: "Do you offer teeth whitening?",       agent: "Yes — we offer in-office whitening for $299 with same-day results." },
    { lang: "Arabic",   flag: "🇸🇦", caller: "هل لديكم تأمين أسنان؟",                agent: "نعم، نقبل معظم خطط التأمين. أرسل لي رقم البطاقة وسأتحقق." },
    { lang: "French",   flag: "🇫🇷", caller: "Ouvert le samedi ?",                    agent: "Oui, samedi de 9h à 14h. Souhaitez-vous réserver ?" },
    { lang: "German",   flag: "🇩🇪", caller: "Kann ich Termine online buchen?",      agent: "Ja, gerne — wann passt es Ihnen am besten?" },
    { lang: "Spanish",  flag: "🇪🇸", caller: "¿Aceptan pacientes nuevos?",            agent: "Sí, encantados de recibirle. ¿Para qué fecha le conviene?" },
    { lang: "Hindi",    flag: "🇮🇳", caller: "क्या आज अपॉइंटमेंट मिल सकती है?",         agent: "बिल्कुल — दोपहर 3 बजे एक स्लॉट खाली है, चलेगा?" },
  ];
  const [idx, setIdx] = React.useState(0);
  const [phase, setPhase] = React.useState("listen"); // listen → think → speak
  React.useEffect(() => {
    const cycle = [
      { phase: "listen", ms: 2200 },
      { phase: "think",  ms: 1100 },
      { phase: "speak",  ms: 3000 },
    ];
    let i = 0;
    const tick = () => {
      const step = cycle[i % cycle.length];
      setPhase(step.phase);
      const t = setTimeout(() => {
        i++;
        if (i % cycle.length === 0) setIdx((x) => (x + 1) % TURNS.length);
        tick();
      }, step.ms);
      return t;
    };
    const handle = tick();
    return () => clearTimeout(handle);
  }, [TURNS.length]);

  const turn = TURNS[idx];
  const isRTL = turn.lang === "Arabic";
  return (
    <div className="relative" data-testid="voice-demo-card">
      {/* Subtle gradient glow behind the card */}
      <div className="absolute -inset-2 bg-gradient-to-br from-[#0044FF]/20 via-[#7C3AED]/15 to-[#EC4899]/20 blur-2xl opacity-70" aria-hidden />
      <div className="relative rounded-[6px] bg-white/95 backdrop-blur-xl shadow-2xl border border-white/40 overflow-hidden">
        {/* Header strip */}
        <div className="px-6 py-4 bg-gradient-to-r from-[#0F1115] via-[#1E293B] to-[#0F1115] text-white flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#10B981] opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#10B981]" />
            </span>
            <div className="text-xs font-mono uppercase tracking-widest">Live multilingual voice</div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-sm font-display font-bold" data-testid="voice-demo-lang">
            <span className="text-base">{turn.flag}</span>
            <span>{turn.lang}</span>
          </div>
        </div>

        {/* Animated 3-stage flow */}
        <div className="px-6 pt-8 pb-6 grid grid-cols-[1fr_auto_1fr_auto_1fr] items-center gap-3">
          <Stage label="Caller" icon={Mic} active={phase === "listen"} color="#10B981" pulse />
          <Arrow active={phase === "listen" || phase === "think"} />
          <Stage label="RAG brain" icon={Sparkles} active={phase === "think"} color="#7C3AED" gradient />
          <Arrow active={phase === "think" || phase === "speak"} />
          <Stage label="Agent" icon={Mic} active={phase === "speak"} color="#0044FF" wave={phase === "speak"} />
        </div>

        {/* Transcript bubbles */}
        <div className="px-6 pb-6 min-h-[170px] space-y-3">
          <div className={`flex gap-2.5 ${isRTL ? "flex-row-reverse" : ""}`}>
            <div className="w-7 h-7 rounded-full bg-[#F3F4F6] flex items-center justify-center text-[10px] font-bold text-[#0F1115] shrink-0">C</div>
            <div className={`flex-1 px-4 py-2.5 rounded-2xl rounded-tl-sm bg-[#F3F4F6] text-[14px] leading-relaxed ${isRTL ? "text-right rounded-tr-sm rounded-tl-2xl" : ""}`} dir={isRTL ? "rtl" : "ltr"}>
              {turn.caller}
            </div>
          </div>
          <div className={`flex gap-2.5 ${isRTL ? "flex-row-reverse" : ""}`}>
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#0044FF] to-[#7C3AED] flex items-center justify-center shrink-0">
              <Sparkles className="w-3.5 h-3.5 text-white" />
            </div>
            <div className={`flex-1 px-4 py-2.5 rounded-2xl rounded-tl-sm bg-gradient-to-br from-[#0044FF]/8 to-[#7C3AED]/8 border border-[#0044FF]/15 text-[14px] leading-relaxed text-[#0F1115] ${isRTL ? "text-right rounded-tr-sm rounded-tl-2xl" : ""}`} dir={isRTL ? "rtl" : "ltr"}>
              {phase === "listen" ? <span className="text-[#4B5563] italic">listening…</span> :
               phase === "think"  ? <span className="text-[#7C3AED] italic">searching knowledge base…</span> :
               turn.agent}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-[#E2E8F0] bg-[#F9FAFB] flex items-center justify-between text-[11px] font-mono">
          <span className="text-[#4B5563]">trained on your site · 6 languages live</span>
          <span className="text-[#0044FF] font-semibold">~{phase === "think" ? "1.0" : "0.4"}s reply</span>
        </div>
      </div>
    </div>
  );
}

function Stage({ label, icon: Icon, active, color, pulse, gradient, wave }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <div
        className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 ${active ? "scale-110 shadow-lg" : "scale-95 opacity-50"} ${pulse && active ? "animate-pulse" : ""} ${gradient && active ? "animate-spin" : ""}`}
        style={{
          background: active
            ? (gradient ? "linear-gradient(135deg,#7C3AED 0%,#EC4899 100%)" : color)
            : "#F3F4F6",
          color: active ? "#FFFFFF" : "#9CA3AF",
          animationDuration: gradient ? "1.6s" : undefined,
          boxShadow: active ? `0 8px 24px ${color}40` : undefined,
        }}
      >
        <Icon className="w-6 h-6" />
      </div>
      <div className={`text-[10px] font-mono uppercase tracking-widest ${active ? "text-[#0F1115] font-semibold" : "text-[#4B5563]"}`}>{label}</div>
      {wave && (
        <div className="flex items-end gap-0.5 h-3 mt-0.5">
          {[60, 100, 80, 100, 60].map((h, i) => (
            <span key={i} className="w-0.5 bg-[#0044FF] animate-hs-wave" style={{ animationDelay: `${i * 100}ms`, height: `${h}%` }} />
          ))}
        </div>
      )}
    </div>
  );
}

function Arrow({ active }) {
  return (
    <div className={`text-xl transition-colors ${active ? "text-[#7C3AED]" : "text-[#E2E8F0]"}`}>→</div>
  );
}


function PricingCalculator() {
  // All sliders start at 0 so the calculator shows $0 until visitor opts in.
  // Once a slider crosses the free tier, the minimum charge per channel is one pack ($9.99).
  const [chats, setChats] = React.useState(0);
  const [webVoice, setWebVoice] = React.useState(0);
  const [phone, setPhone] = React.useState(0);
  const [scrapes, setScrapes] = React.useState(0);
  const c = React.useMemo(() => packCost(chats, PACK.chat), [chats]);
  const w = React.useMemo(() => packCost(webVoice, PACK.webVoice), [webVoice]);
  const p = React.useMemo(() => packCost(phone, PACK.phone), [phone]);
  const s = React.useMemo(() => packCost(scrapes, PACK.scrape), [scrapes]);
  const total = (c.cost + w.cost + p.cost + s.cost).toFixed(2);
  return (
    <div className="hs-card p-8 mt-12" data-testid="pricing-calculator">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono uppercase text-[#4B5563]">cost calculator</div>
          <div className="font-display font-extrabold text-2xl mt-1">Drag the sliders to your usage</div>
        </div>
        <div className="text-right">
          <div className="text-xs font-mono text-[#4B5563]">estimated monthly cost</div>
          <div className="font-display font-extrabold text-5xl" data-testid="calc-total">${total}</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-x-10 gap-y-7 mt-10">
        <SliderRow label="Web chats / month" value={chats} setValue={setChats}
                   min={0} max={30000} step={50} suffix="chats" testid="calc-chats"
                   price={chats === 0 ? "no usage" : `$${c.cost.toFixed(2)} / ${chats.toLocaleString()} chats`}
                   note="Free 10 · then min pack $9.99 / 3,000 chats · overage $0.003/chat" />
        <SliderRow label="Web voice / month" value={webVoice} setValue={setWebVoice}
                   min={0} max={1000} step={1} suffix="min" testid="calc-webvoice"
                   price={webVoice === 0 ? "no usage" : `$${w.cost.toFixed(2)} / ${webVoice} min`}
                   note="Free 10 min · then min pack $9.99 / 100 min · overage $0.10/min" />
        <SliderRow label="AI phone calls / month" value={phone} setValue={setPhone}
                   min={0} max={500} step={1} suffix="min" testid="calc-phone"
                   price={phone === 0 ? "no usage" : `$${p.cost.toFixed(2)} / ${phone} min`}
                   note="Min pack $9.99 / 25 min · overage $0.40/min" />
        <SliderRow label="Extra AI agents" value={scrapes} setValue={setScrapes}
                   min={0} max={20} step={1} suffix="agents" testid="calc-scrapes"
                   price={scrapes === 0 ? "1 agent included free" : `$${s.cost.toFixed(2)} / ${scrapes} extra agents`}
                   note="$1.00 per extra agent (incl. 1 scraped website)" />
      </div>

      <div className="mt-8 pt-6 border-t border-[#E2E8F0] flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs text-[#4B5563]">No subscriptions. Pay only when you need more. Credits never expire.</div>
        <Link to="/signup" className="btn-primary text-sm" data-testid="calc-cta">Start with free trial</Link>
      </div>
    </div>
  );
}

function SliderRow({ label, value, setValue, min = 0, max, step, suffix, price, note, testid }) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-sm font-medium">{label}</div>
        <div className="font-display font-bold text-xl" data-testid={`${testid}-value`}>{value.toLocaleString()}<span className="text-xs text-[#4B5563] font-mono font-normal ml-1">{suffix}</span></div>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => setValue(parseInt(e.target.value, 10))}
        className="w-full mt-2 accent-[#0044FF]"
        data-testid={testid}
      />
      <div className="flex items-center justify-between mt-1 gap-3">
        <div className="text-[10px] font-mono text-[#4B5563]">{note}</div>
        <div className="text-[11px] font-mono text-[#0044FF] font-bold whitespace-nowrap" data-testid={`${testid}-price`}>{price}</div>
      </div>
    </div>
  );
}

/* ------------ Platform brand glyphs (compact monochrome SVG) ------------ */
const BrandIcon = ({ children, color }) => (
  <svg viewBox="0 0 24 24" className="w-7 h-7" fill={color} aria-hidden="true">{children}</svg>
);

const Platforms = [
  {
    id: "wordpress",
    name: "WordPress",
    install: "1-click plugin",
    accent: "#21759B",
    bg: "linear-gradient(135deg,#21759B 0%,#1D5B7E 100%)",
    icon: (
      <BrandIcon color="#21759B">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-8.7 10c0-1.27.27-2.47.76-3.56l4.2 11.49C5.07 18.31 3.3 15.36 3.3 12zM12 20.7c-.88 0-1.72-.13-2.51-.36l2.66-7.74 2.73 7.47.06.13c-.93.32-1.92.5-2.94.5zm1.22-12.78c.53-.03 1.01-.09 1.01-.09.48-.06.42-.76-.05-.74 0 0-1.43.11-2.36.11-.87 0-2.33-.11-2.33-.11-.47-.02-.53.71-.05.74 0 0 .45.06.93.09l1.34 3.68-1.88 5.65-3.13-9.33c.53-.03 1.01-.09 1.01-.09.48-.06.42-.76-.05-.74 0 0-1.43.11-2.36.11-.17 0-.36 0-.57-.01 1.61-2.45 4.4-4.07 7.57-4.07 2.36 0 4.51.9 6.13 2.38-.04 0-.08-.01-.13-.01-.87 0-1.49.76-1.49 1.58 0 .73.42 1.35.87 2.08.34.59.74 1.34.74 2.43 0 .75-.29 1.62-.67 2.84l-.87 2.92-3.18-9.4zm7.61 3.81c.62-.06.99-.4.99-.94 0-.46-.31-.85-.78-1.27.06-.41.13-.86.13-1.34 0-.13-.01-.27-.03-.4.84 1.55 1.32 3.32 1.32 5.21 0 4.02-2.18 7.53-5.41 9.41l3.31-9.55c.18-.4.27-.74.27-1.06z"/>
      </BrandIcon>
    ),
  },
  {
    id: "shopify",
    name: "Shopify",
    install: "1-click app",
    accent: "#95BF47",
    bg: "linear-gradient(135deg,#95BF47 0%,#5E8E3E 100%)",
    icon: (
      <BrandIcon color="#95BF47">
        <path d="M15.337 4.064c-.075-.01-.16-.01-.235-.01h-.01s-.196.062-.515.16c-.31-.892-.864-1.717-2-1.717h-.1C12.18 2.222 11.78 2 11.43 2c-2.745.01-4.06 3.435-4.475 5.176-1.067.33-1.834.567-1.92.598-.598.187-.618.207-.69.762C4.293 8.95 2.73 21.041 2.73 21.041L14.555 23l6.41-1.387S15.41 4.074 15.337 4.064zM13.65 4.55c-.547.17-1.17.362-1.844.567v-.392c0-1.17-.16-2.124-.42-2.873.99.124 1.65 1.243 2.265 2.698zm-3.197-2.426c.298.764.487 1.847.487 3.293v.124c-1.16.36-2.42.752-3.685 1.144C7.965 4.45 9.27 2.443 10.453 2.124zM9.245 2.81c.298 0 .597.041.866.124-1.232 1.005-2.493 3.011-3.187 6.012-.926.287-1.832.567-2.665.836.515-2.123 1.928-6.972 4.986-6.972zm6.5 19.087-9.965-2.07s.876-13.79.886-13.844c.082-.062 1.69-.526 2.235-.69.082-.02.16-.062.235-.082 1.16-.36 2.42-.752 3.685-1.144.298 0 .597-.041.866-.062 1.083 1.067 1.572 3.012 1.572 4.892l-.01 1.305c.16-.062.32-.124.495-.186.32-.124.598-.207.876-.287.246-.082.476-.144.69-.207.236-.062.46-.124.69-.187 4.207-1.06 4.74-1.16 4.74-1.16z"/>
      </BrandIcon>
    ),
  },
  {
    id: "magento",
    name: "Magento",
    install: "Extension",
    accent: "#EE672F",
    bg: "linear-gradient(135deg,#EE672F 0%,#C7522A 100%)",
    icon: (
      <BrandIcon color="#EE672F">
        <path d="M12 0 1.6 6v12L12 24l10.4-6V6L12 0zm-1.85 18.45h-2.6V9.05L12 6.7l4.45 2.35v9.4h-2.6v-7.8L12 9.45l-1.85.85v8.15z"/>
      </BrandIcon>
    ),
  },
  {
    id: "bigcommerce",
    name: "BigCommerce",
    install: "App store",
    accent: "#121118",
    bg: "linear-gradient(135deg,#34313F 0%,#121118 100%)",
    icon: (
      <BrandIcon color="#34313F">
        <path d="M23.692 8.481 15.534.323A.97.97 0 0 0 14.85 0H1.13C.51 0 0 .51 0 1.13v13.708a.99.99 0 0 0 .323.724l8.158 8.158a.97.97 0 0 0 .684.28h13.685a1.14 1.14 0 0 0 1.142-1.142V9.165a.97.97 0 0 0-.3-.684zM12.343 17.7H6.07v-2.077h6.27c.776 0 1.413-.555 1.413-1.331 0-.776-.637-1.33-1.413-1.33H6.07v-1.978h6.165c1.83 0 3.302 1.083 3.302 2.951 0 1.146-.728 2.115-1.62 2.523.972.385 1.755 1.379 1.755 2.616-.001 1.992-1.485 3.075-3.33 3.075z"/>
      </BrandIcon>
    ),
  },
  {
    id: "wix",
    name: "Wix",
    install: "Dev-mode embed",
    accent: "#0C6EBD",
    bg: "linear-gradient(135deg,#FFC233 0%,#FAAD4D 60%,#F0772B 100%)",
    icon: (
      <BrandIcon color="#0C6EBD">
        <path d="M5.232 6.222 2.83 17.778h2.952l1.473-7.318 1.93 7.318h2.45L13.563 10.46l1.473 7.318h2.952l-2.4-11.556h-3.06L10.486 14.4 8.392 6.222zM21.17 6.222h-2.952v11.556h2.952z"/>
      </BrandIcon>
    ),
  },
  {
    id: "squarespace",
    name: "Squarespace",
    install: "Code injection",
    accent: "#000000",
    bg: "linear-gradient(135deg,#1A1A1A 0%,#000000 100%)",
    icon: (
      <BrandIcon color="#000000">
        <path d="M17.752 11.32 11.146 17.93a3.752 3.752 0 1 1-5.305-5.305l5.078-5.078a1.876 1.876 0 1 1 2.653 2.653l-4.452 4.451a.626.626 0 1 1-.884-.884l3.535-3.535-.884-.884-3.535 3.535a1.876 1.876 0 1 0 2.652 2.652l4.452-4.451a3.128 3.128 0 1 0-4.422-4.421L4.957 11.74a5.005 5.005 0 0 0 7.077 7.077l6.61-6.61z"/>
      </BrandIcon>
    ),
  },
  {
    id: "php",
    name: "PHP",
    install: "SDK + snippet",
    accent: "#777BB4",
    bg: "linear-gradient(135deg,#8993BE 0%,#4F5B93 100%)",
    icon: (
      <BrandIcon color="#777BB4">
        <ellipse cx="12" cy="12" rx="11" ry="6" fill="none" stroke="#777BB4" strokeWidth="1.5"/>
        <path d="M5.5 9.5h2.3c1 0 1.5.4 1.5 1.2 0 1.2-.7 1.8-2 1.8H6.7l-.3 1.5h-.9zm.9.7-.3 1.6h.7c.8 0 1.3-.3 1.3-1 0-.5-.3-.6-.9-.6zM10.4 8.3h.9l-.3 1.2h1c1 0 1.5.4 1.4 1.1 0 .2-.1.5-.2.7l-.6 2.7h-.9l.5-2.5c.1-.4 0-.6-.5-.6h-.9l-.7 3.1h-.9zM15.3 9.5h2.3c1 0 1.5.4 1.5 1.2 0 1.2-.7 1.8-2 1.8h-1.3l-.3 1.5h-.9zm.9.7-.3 1.6h.7c.8 0 1.3-.3 1.3-1 0-.5-.3-.6-.9-.6z"/>
      </BrandIcon>
    ),
  },
  {
    id: "nodejs",
    name: "Node.js",
    install: "npm package",
    accent: "#339933",
    bg: "linear-gradient(135deg,#83CD29 0%,#339933 100%)",
    icon: (
      <BrandIcon color="#339933">
        <path d="M11.998 24a2.21 2.21 0 0 1-1.099-.293L7.4 21.633c-.523-.293-.267-.397-.095-.456.698-.243.84-.3 1.585-.722.078-.044.18-.026.26.022l2.688 1.595a.34.34 0 0 0 .325 0l10.48-6.05a.33.33 0 0 0 .16-.285V3.65a.34.34 0 0 0-.163-.292L11.97.317a.33.33 0 0 0-.323 0L1.187 6.357a.34.34 0 0 0-.166.292v12.087c0 .117.063.227.166.283l2.873 1.66c1.56.78 2.514-.138 2.514-1.06V7.69c0-.17.137-.302.306-.302h1.328c.166 0 .306.133.306.302v11.93c0 2.08-1.133 3.273-3.105 3.273-.607 0-1.084 0-2.42-.658L.227 20.65A2.21 2.21 0 0 1-.871 18.74V6.65A2.21 2.21 0 0 1 .227 4.738L10.71-1.297a2.29 2.29 0 0 1 2.196 0l10.48 6.04A2.21 2.21 0 0 1 24.5 6.65v12.09a2.22 2.22 0 0 1-1.094 1.91l-10.48 6.05A2.27 2.27 0 0 1 11.998 24zm3.236-8.32c-4.587 0-5.55-2.106-5.55-3.874a.3.3 0 0 1 .3-.302h1.355a.3.3 0 0 1 .3.255c.205 1.38.815 2.077 3.595 2.077 2.21 0 3.155-.5 3.155-1.677 0-.677-.267-1.18-3.705-1.518-2.874-.284-4.65-.92-4.65-3.22 0-2.12 1.788-3.385 4.787-3.385 3.37 0 5.04 1.17 5.25 3.68a.3.3 0 0 1-.075.235.3.3 0 0 1-.225.103h-1.36a.3.3 0 0 1-.295-.236c-.328-1.452-1.122-1.917-3.295-1.917-2.43 0-2.713.846-2.713 1.48 0 .768.334 1 3.594 1.435 3.225.428 4.755 1.034 4.755 3.3-.002 2.29-1.912 3.604-5.245 3.604z" transform="translate(0 1)"/>
      </BrandIcon>
    ),
  },
  {
    id: "static",
    name: "Static HTML",
    install: "Copy-paste snippet",
    accent: "#E34F26",
    bg: "linear-gradient(135deg,#F06529 0%,#E34F26 100%)",
    icon: (
      <BrandIcon color="#E34F26">
        <path d="M1.5 0h21l-1.91 21.563L12 24l-8.59-2.438zm7.31 9.875v3h6.94l-.31 3.5L12 17.25l-3.5-1L8.27 13.5H5.41l.5 5.563L12 20.875l6.09-1.813.81-9.187z"/>
      </BrandIcon>
    ),
  },
  {
    id: "other",
    name: "Anything else",
    install: "REST + Webhook",
    accent: "#7C3AED",
    bg: "linear-gradient(135deg,#7C3AED 0%,#EC4899 100%)",
    icon: (
      <BrandIcon color="#7C3AED">
        <path d="M9.4 16.6 4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0L19.2 12l-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/>
      </BrandIcon>
    ),
  },
];

function PlatformIntegrations() {
  return (
    <section id="integrations" className="relative py-24 lg:py-32 border-b border-[#E2E8F0] overflow-hidden bg-white" data-testid="integrations-section">
      {/* Soft decorative gradient */}
      <div className="absolute -top-24 right-0 w-[420px] h-[420px] rounded-full blur-3xl opacity-30 pointer-events-none"
           style={{ background: "radial-gradient(circle,#0044FF 0%,transparent 70%)" }} aria-hidden />
      <div className="absolute -bottom-24 -left-24 w-[420px] h-[420px] rounded-full blur-3xl opacity-25 pointer-events-none"
           style={{ background: "radial-gradient(circle,#10B981 0%,transparent 70%)" }} aria-hidden />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <span className="text-xs font-mono text-[#4B5563]">// 03</span>
          <h2 className="font-display font-bold text-4xl md:text-5xl mt-3">
            Plug into <span className="bg-gradient-to-r from-[#0044FF] via-[#7C3AED] to-[#EC4899] bg-clip-text text-transparent">any stack</span> in minutes.
          </h2>
          <p className="text-[#4B5563] mt-4 text-lg">
            Embed your AI agent with a one-click plugin, a copy-paste snippet, or our REST API.
            Agents read &amp; write to your live database — products, orders, bookings, anything.
          </p>
        </div>

        {/* Three capability highlight cards */}
        <div className="grid md:grid-cols-3 gap-5 mt-12" data-testid="integration-capabilities">
          <CapabilityCard
            testid="cap-plugin"
            Icon={MousePointerClick}
            accent="#10B981"
            tag="ZERO CODE"
            title="One-click plugin install"
            desc="Drop our official plugin from the WordPress, Shopify and BigCommerce marketplaces. Auto-configured to your store in under 60 seconds."
          />
          <CapabilityCard
            testid="cap-snippet"
            Icon={Code2}
            accent="#0044FF"
            tag="COPY · PASTE · LIVE"
            title="Drop-in code snippet"
            desc="A 4-line script tag works on any HTML page — Wix, Squarespace, Webflow, static sites and custom builds alike."
            extra={
              <pre className="mt-4 px-3 py-2.5 rounded-[4px] bg-[#0F1115] text-[11px] leading-relaxed font-mono text-[#E2E8F0] overflow-x-auto border border-[#1E293B]">
<span className="text-[#94A3B8]">{"<!-- HelloSir.ai widget -->"}</span>{"\n"}
<span className="text-[#FACC15]">{"<script "}</span>
<span className="text-[#A5F3FC]">src</span>=<span className="text-[#86EFAC]">{"\"hellosir.ai/embed.js\""}</span>{"\n        "}
<span className="text-[#A5F3FC]">data-agent</span>=<span className="text-[#86EFAC]">{"\"your_agent_id\""}</span>
<span className="text-[#FACC15]">{">"}</span>{"\n"}
<span className="text-[#FACC15]">{"</script>"}</span>
              </pre>
            }
          />
          <CapabilityCard
            testid="cap-db"
            Icon={Database}
            accent="#7C3AED"
            tag="LIVE DATA"
            title="Talks to your database"
            desc="Connect MySQL, Postgres, MongoDB, REST or GraphQL. Agents query inventory, look up orders, check appointments and update records — all in real time."
          />
        </div>

        {/* Platform grid */}
        <div className="mt-16">
          <div className="flex items-center gap-3 mb-6">
            <Zap className="w-4 h-4 text-[#F97316]" />
            <span className="text-xs font-mono uppercase tracking-widest text-[#4B5563]">
              works everywhere — pick your platform
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4" data-testid="platform-grid">
            {Platforms.map((p) => (
              <PlatformCard key={p.id} platform={p} />
            ))}
          </div>

          {/* CTA strip */}
          <div className="mt-10 flex flex-wrap items-center justify-between gap-4 px-6 py-5 rounded-[6px] bg-gradient-to-r from-[#0F1115] via-[#1E293B] to-[#0F1115] text-white">
            <div className="flex items-center gap-3">
              <Copy className="w-5 h-5 text-[#10B981]" />
              <div>
                <div className="font-display font-bold text-base">No vendor lock-in.</div>
                <div className="text-xs font-mono text-[#94A3B8] mt-0.5">Move between snippet, plugin or API at any time — your knowledge base stays put.</div>
              </div>
            </div>
            <Link to="/signup" className="inline-flex items-center gap-2 text-sm font-medium bg-white text-[#0F1115] hover:bg-[#F3F4F6] px-5 py-2.5 rounded-[4px] transition-colors" data-testid="integrations-cta">
              Embed in your site <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function CapabilityCard({ Icon, accent, tag, title, desc, extra, testid }) {
  return (
    <div
      className="group relative p-7 rounded-[6px] bg-white border border-[#E2E8F0] hover:border-[#030712] transition-all duration-300 hover:shadow-[0_20px_40px_-12px_rgba(2,6,23,0.12)] hover:-translate-y-1"
      data-testid={testid}
    >
      <div
        className="w-12 h-12 rounded-[6px] flex items-center justify-center mb-5"
        style={{ background: `${accent}14`, color: accent }}
      >
        <Icon className="w-6 h-6" />
      </div>
      <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: accent }}>{tag}</div>
      <h3 className="font-display font-bold text-xl mt-2">{title}</h3>
      <p className="text-sm text-[#4B5563] mt-2 leading-relaxed">{desc}</p>
      {extra}
      <div
        className="absolute inset-x-0 bottom-0 h-0.5 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"
        style={{ background: `linear-gradient(90deg, ${accent}, transparent)` }}
      />
    </div>
  );
}

function PlatformCard({ platform }) {
  return (
    <div
      className="group relative p-5 rounded-[6px] bg-white border border-[#E2E8F0] hover:border-transparent transition-all duration-300 hover:shadow-[0_18px_36px_-12px_rgba(2,6,23,0.18)] hover:-translate-y-1 overflow-hidden"
      data-testid={`platform-${platform.id}`}
    >
      {/* Hover gradient wash */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{ background: platform.bg }}
        aria-hidden
      />
      <div className="relative">
        <div className="w-11 h-11 rounded-[4px] bg-[#F9FAFB] group-hover:bg-white/95 backdrop-blur flex items-center justify-center transition-colors">
          {platform.icon}
        </div>
        <div className="font-display font-bold text-base mt-4 group-hover:text-white transition-colors">{platform.name}</div>
        <div
          className="text-[10px] font-mono uppercase tracking-widest mt-1 transition-colors group-hover:text-white/80"
          style={{ color: platform.accent }}
        >
          {platform.install}
        </div>
      </div>
    </div>
  );
}

/* ------------------- Agents Can ______ (typewriter) ------------------- */
const AGENT_VERBS = [
  { text: "book appointments.",        color: "#10B981", Icon: CalendarCheck },
  { text: "read your database.",       color: "#7C3AED", Icon: Database },
  { text: "suggest products.",         color: "#F97316", Icon: ShoppingBag },
  { text: "answer in any language.",   color: "#0044FF", Icon: MessageSquare },
  { text: "qualify your leads.",       color: "#EC4899", Icon: TrendingUp },
  { text: "close sales for you.",      color: "#EF4444", Icon: Target },
  { text: "handle patient intake.",    color: "#06B6D4", Icon: Stethoscope },
  { text: "take reservations.",        color: "#F59E0B", Icon: UtensilsCrossed },
  { text: "capture admissions.",       color: "#8B5CF6", Icon: GraduationCap },
  { text: "look up live orders.",      color: "#0F766E", Icon: Package },
];

const CAPABILITY_CARDS = [
  { title: "Book appointments",     desc: "Books, confirms, reschedules and cancels — synced to your calendar.", color: "#10B981", Icon: CalendarCheck },
  { title: "Read your database",    desc: "Live queries to MySQL, Postgres, Mongo or REST. Real answers, not hallucinations.", color: "#7C3AED", Icon: Database },
  { title: "Suggest products",      desc: "Personalised picks from your catalog with stock, price and image.", color: "#F97316", Icon: ShoppingBag },
  { title: "Provide information",   desc: "Trained on your site, PDFs, FAQs and policies. Always on-message.", color: "#0044FF", Icon: BookOpen },
  { title: "Qualify leads",         desc: "Identifies prospects, runs BANT, captures contact + intent.", color: "#EC4899", Icon: TrendingUp },
  { title: "Close sales",           desc: "Persuasive but honest. Handles objections and pushes to checkout.", color: "#EF4444", Icon: Target },
  { title: "Customer support",      desc: "Resolves issues, answers questions, escalates only when needed.", color: "#06B6D4", Icon: Headphones },
  { title: "Collect structured info", desc: "Gathers names, emails, IDs, addresses — straight into your CRM.", color: "#F59E0B", Icon: ClipboardList },
];

function useTypewriter(phrases, { typeMs = 55, holdMs = 1400, eraseMs = 28 } = {}) {
  const [idx, setIdx] = React.useState(0);
  const [text, setText] = React.useState("");
  const [phase, setPhase] = React.useState("typing"); // typing → holding → erasing

  React.useEffect(() => {
    const full = phrases[idx % phrases.length].text;
    let t;
    if (phase === "typing") {
      if (text.length < full.length) {
        t = setTimeout(() => setText(full.slice(0, text.length + 1)), typeMs);
      } else {
        t = setTimeout(() => setPhase("erasing"), holdMs);
      }
    } else if (phase === "erasing") {
      if (text.length > 0) {
        t = setTimeout(() => setText(full.slice(0, text.length - 1)), eraseMs);
      } else {
        setIdx((i) => (i + 1) % phrases.length);
        setPhase("typing");
      }
    }
    return () => clearTimeout(t);
  }, [text, phase, idx, phrases, typeMs, holdMs, eraseMs]);

  return { text, current: phrases[idx % phrases.length] };
}

function AgentsCanSection() {
  const { text, current } = useTypewriter(AGENT_VERBS);
  const Icon = current.Icon;

  return (
    <section id="capabilities" className="relative py-24 lg:py-32 border-b border-[#E2E8F0] overflow-hidden bg-[#0F1115] text-white" data-testid="capabilities-section">
      {/* Subtle grid texture */}
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
        aria-hidden
      />
      {/* Color-shifting orb that picks up the current phrase color */}
      <div
        className="absolute -top-32 left-1/2 -translate-x-1/2 w-[720px] h-[720px] rounded-full blur-[140px] opacity-30 transition-colors duration-700 pointer-events-none"
        style={{ background: current.color }}
        aria-hidden
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white/50">
          <span>// 04</span>
          <span className="text-white/30">·</span>
          <span>what your agent does</span>
        </div>

        {/* Inline typewriter headline — never wraps; horizontally scrolls if extremely narrow */}
        <h2 className="font-display font-extrabold mt-6 leading-[1.05] tracking-tight text-3xl sm:text-4xl md:text-5xl lg:text-6xl whitespace-nowrap overflow-x-auto pb-1 -mb-1">
          <span className="text-white/95">Agents can</span>
          <span
            className="inline-flex items-center justify-center w-9 h-9 sm:w-11 sm:h-11 md:w-12 md:h-12 lg:w-14 lg:h-14 mx-3 md:mx-4 rounded-[6px] shrink-0 align-[-0.2em] transition-colors duration-700"
            style={{ background: `${current.color}25`, color: current.color }}
            data-testid="capabilities-icon"
          >
            <Icon className="w-5 h-5 md:w-6 md:h-6 lg:w-7 lg:h-7" />
          </span>
          <span
            className="inline transition-colors duration-700"
            style={{ color: current.color }}
            data-testid="capabilities-typewriter"
          >
            {text}
            <span
              className="inline-block ml-1 w-[3px] md:w-[4px] h-[0.85em] align-[-0.05em] animate-hs-caret"
              style={{ background: current.color }}
              aria-hidden
            />
          </span>
        </h2>

        <p className="text-white/60 text-lg mt-8 max-w-2xl">
          One agent, every job your team would normally split across departments.
          Pick the skills you want — turn them on with a single click.
        </p>

        {/* Capability grid */}
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="capability-grid">
          {CAPABILITY_CARDS.map((c) => (
            <CapabilityTile key={c.title} {...c} />
          ))}
        </div>

        {/* CTA strip */}
        <div className="mt-12 flex flex-wrap items-center justify-between gap-4 px-6 py-5 rounded-[6px] border border-white/10 bg-white/[0.04] backdrop-blur">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-[6px] bg-gradient-to-br from-[#0044FF] to-[#7C3AED] flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-display font-bold text-base">Mix & match in the agent builder.</div>
              <div className="text-xs font-mono text-white/60 mt-0.5">Combine skills, set guardrails, and ship to chat, voice and phone in one go.</div>
            </div>
          </div>
          <Link to="/signup" className="inline-flex items-center gap-2 text-sm font-medium bg-white text-[#0F1115] hover:bg-[#F3F4F6] px-5 py-2.5 rounded-[4px] transition-colors" data-testid="capabilities-cta">
            Build your agent <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function CapabilityTile({ title, desc, color, Icon }) {
  return (
    <div
      className="group relative p-6 rounded-[6px] border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition-all duration-300 hover:-translate-y-1 overflow-hidden"
      data-testid={`capability-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div
        className="absolute inset-x-0 top-0 h-px scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"
        style={{ background: `linear-gradient(90deg, ${color}, transparent)` }}
      />
      <div
        className="w-11 h-11 rounded-[6px] flex items-center justify-center"
        style={{ background: `${color}20`, color }}
      >
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="font-display font-bold text-lg mt-5 text-white">{title}</h3>
      <p className="text-sm text-white/60 mt-2 leading-relaxed">{desc}</p>
    </div>
  );
}

/* -------------------- Knowledge feature card (animated) -------------------- */
const KNOWLEDGE_FEATURES = [
  {
    key: "crawl",
    Icon: Globe,
    color: "#0044FF",
    title: "Auto-crawl your website + sitemap",
    detail: "Point us at your URL — we'll discover every page via sitemap.xml and keep the knowledge fresh on a schedule.",
    visual: "crawl",
  },
  {
    key: "upload",
    Icon: FileText,
    color: "#7C3AED",
    title: "Upload PDFs, docs, CSVs, Notion exports",
    detail: "Drag-and-drop your brochures, price-sheets, FAQs and exports. We chunk, embed and index them in seconds.",
    visual: "files",
  },
  {
    key: "semantic",
    Icon: Brain,
    color: "#EC4899",
    title: "Search by semantic similarity in milliseconds",
    detail: "Vector search finds the right passage even when the customer's words don't match yours.",
    visual: "search",
  },
  {
    key: "retrain",
    Icon: RefreshCw,
    color: "#10B981",
    title: "Re-train daily, weekly, or on push",
    detail: "Set a refresh cadence — or trigger from your CMS so updates go live the moment you publish.",
    visual: "retrain",
  },
  {
    key: "leads",
    Icon: Users,
    color: "#F97316",
    title: "Lead capture written straight into your CRM",
    detail: "Every qualified conversation ends with a lead written to HubSpot, Salesforce, Pipedrive or any webhook.",
    visual: "leads",
  },
];

function KnowledgeFeatureCard() {
  const [idx, setIdx] = React.useState(0);
  React.useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % KNOWLEDGE_FEATURES.length), 2800);
    return () => clearInterval(t);
  }, []);
  const f = KNOWLEDGE_FEATURES[idx];
  const F = f.Icon;
  return (
    <div className="relative rounded-[6px] border border-[#E2E8F0] bg-white shadow-xl overflow-hidden" data-testid="knowledge-feature-card">
      {/* Header strip */}
      <div className="px-5 py-3 border-b border-[#E2E8F0] bg-[#0F1115] text-white flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className="w-2.5 h-2.5 rounded-full bg-[#10B981] animate-pulse" />
          <span className="text-xs font-mono uppercase tracking-widest">training pipeline</span>
        </div>
        <span className="text-[11px] font-mono text-white/60">step {idx + 1} / {KNOWLEDGE_FEATURES.length}</span>
      </div>

      {/* Animated visual */}
      <div className="relative aspect-[5/4] bg-gradient-to-br from-[#F0F4FF] via-white to-[#FFF1F8] overflow-hidden">
        {/* Color orb */}
        <div
          key={f.key}
          className="absolute inset-0 transition-colors duration-700"
          aria-hidden
        >
          <div
            className="absolute -top-24 -right-24 w-[360px] h-[360px] rounded-full blur-3xl opacity-40 transition-colors duration-700"
            style={{ background: `radial-gradient(circle, ${f.color} 0%, transparent 70%)` }}
          />
        </div>

        {/* Per-feature scene */}
        <div className="relative h-full p-7 flex flex-col">
          <div
            className="w-14 h-14 rounded-[8px] flex items-center justify-center transition-colors duration-700 shadow-lg"
            style={{ background: f.color, color: "#FFFFFF" }}
          >
            <F className="w-7 h-7" />
          </div>

          <div className="mt-5">
            <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: f.color }}>
              {f.key}
            </div>
            <div className="font-display font-bold text-xl text-[#0F1115] mt-1.5">{f.title}</div>
            <p className="text-sm text-[#4B5563] mt-2 leading-relaxed max-w-md">{f.detail}</p>
          </div>

          {/* Scene-specific micro-animation */}
          <div className="mt-auto pt-4">
            <KnowledgeScene visual={f.visual} color={f.color} />
          </div>
        </div>
      </div>

      {/* Progress dots */}
      <div className="px-5 py-3 border-t border-[#E2E8F0] flex items-center justify-between bg-[#F9FAFB]">
        <div className="flex gap-1.5">
          {KNOWLEDGE_FEATURES.map((k, i) => (
            <button
              key={k.key}
              onClick={() => setIdx(i)}
              aria-label={`Show ${k.title}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? "w-8" : "w-1.5 hover:w-3"}`}
              style={{ background: i === idx ? f.color : "#CBD5E1" }}
            />
          ))}
        </div>
        <span className="text-[11px] font-mono text-[#4B5563]">auto-cycling · click to jump</span>
      </div>
    </div>
  );
}

function KnowledgeScene({ visual, color }) {
  if (visual === "crawl") {
    return (
      <div className="flex items-center gap-2 text-[11px] font-mono">
        {["/", "/products", "/pricing", "/faq", "/contact"].map((p, i) => (
          <span
            key={p}
            className="px-2 py-1 rounded bg-white border border-[#E2E8F0] text-[#0F1115] animate-pulse"
            style={{ animationDelay: `${i * 200}ms`, borderColor: `${color}40` }}
          >
            {p}
          </span>
        ))}
      </div>
    );
  }
  if (visual === "files") {
    return (
      <div className="flex items-end gap-2">
        {[
          { ext: "pdf", c: "#EF4444" },
          { ext: "docx", c: "#2563EB" },
          { ext: "csv", c: "#10B981" },
          { ext: "md", c: "#0F1115" },
        ].map((d, i) => (
          <div
            key={d.ext}
            className="relative w-12 h-14 rounded-sm bg-white border border-[#E2E8F0] flex flex-col items-center justify-end p-1.5 shadow-sm animate-hs-float"
            style={{ animationDelay: `${i * 350}ms` }}
          >
            <div className="absolute top-0 right-0 w-3 h-3 bg-[#F3F4F6] border-l border-b border-[#E2E8F0]" />
            <span className="text-[9px] font-mono font-bold" style={{ color: d.c }}>.{d.ext}</span>
          </div>
        ))}
      </div>
    );
  }
  if (visual === "search") {
    return (
      <div className="space-y-2">
        <div className="px-3 py-2 rounded bg-white border border-[#E2E8F0] text-[12px] font-mono flex items-center gap-2">
          <span style={{ color }}>›</span>
          <span className="text-[#0F1115]">do you ship to dubai?</span>
        </div>
        <div className="flex gap-2">
          {[92, 78, 64].map((score, i) => (
            <div key={i} className="flex-1 px-2 py-1.5 rounded bg-white border border-[#E2E8F0] flex items-center justify-between text-[10px] font-mono">
              <span className="text-[#4B5563]">match #{i + 1}</span>
              <span className="font-bold" style={{ color }}>{score}%</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (visual === "retrain") {
    return (
      <div className="flex items-center gap-3">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d, i) => (
          <div key={d} className="flex flex-col items-center gap-1">
            <div
              className={`w-3 h-3 rounded-sm ${[1, 3, 5].includes(i) ? "" : "opacity-30"}`}
              style={{ background: color }}
            />
            <span className="text-[9px] font-mono text-[#4B5563]">{d}</span>
          </div>
        ))}
        <span className="text-[10px] font-mono text-[#4B5563] ml-2">3× / week</span>
      </div>
    );
  }
  if (visual === "leads") {
    return (
      <div className="space-y-1.5">
        {["Anna Lopez · qualified · email captured", "Mark Chen · demo booked · Tue 3pm", "Priya R · trial started"].map((row, i) => (
          <div key={i} className="px-3 py-1.5 rounded bg-white border border-[#E2E8F0] text-[11px] font-mono text-[#0F1115] flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
            {row}
          </div>
        ))}
      </div>
    );
  }
  return null;
}

/* ----------------------- Clients & Testimonials ----------------------- */
const CLIENTS = [
  { name: "International Education Office",   tag: "UK · Student Consultancy",     mono: "IEO", accent: "#0044FF" },
  { name: "HostNex",                          tag: "Hosting Provider",             mono: "HN",  accent: "#10B981" },
  { name: "Cloud Lab Technologies",           tag: "Software Technology",          mono: "CL",  accent: "#7C3AED" },
  { name: "Edubridge Network",                tag: "EdTech Network",               mono: "EB",  accent: "#F97316" },
  { name: "Doctors for Health",               tag: "UK · Healthcare",              mono: "DfH", accent: "#06B6D4" },
  { name: "NiNi's Tutor",                     tag: "Online Tutoring",              mono: "Nt",  accent: "#EC4899" },
];

const TESTIMONIALS = [
  {
    quote: "HelloSir captures admissions enquiries 24/7 from students across India, Nigeria and the UAE — in their own language. Our team finally only sees the qualified ones.",
    author: "International Education Office",
    role: "Student Consultancy · United Kingdom",
    mono: "IEO",
    accent: "#0044FF",
  },
  {
    quote: "Setup took an afternoon. The bot now handles 70% of tier-1 hosting questions about DNS, SSL and migrations — even at 3am.",
    author: "HostNex",
    role: "Hosting Provider",
    mono: "HN",
    accent: "#10B981",
  },
  {
    quote: "We use HelloSir on our own product site and recommend it to every client we build for. Train once, deploy across chat, web voice and a phone number.",
    author: "Cloud Lab Technologies",
    role: "Software Technology · United Kingdom",
    mono: "CL",
    accent: "#7C3AED",
  },
  {
    quote: "Course advisors used to spend half their day on the same five questions. Now they're closing applications instead. Conversion is up materially this term.",
    author: "Edubridge Network",
    role: "EdTech Network",
    mono: "EB",
    accent: "#F97316",
  },
  {
    quote: "Patients book consultations 24/7 and the agent never gives medical advice — exactly the boundary we needed. The intake forms write straight into our system.",
    author: "Doctors for Health",
    role: "Healthcare · United Kingdom",
    mono: "DfH",
    accent: "#06B6D4",
  },
  {
    quote: "Parents and students chat with the tutor agent before they ever speak to me. Booked-trial conversion jumped within the first two weeks.",
    author: "NiNi's Tutor",
    role: "Online Tutoring",
    mono: "Nt",
    accent: "#EC4899",
  },
];

function ClientLogo({ name, mono, accent, tag }) {
  return (
    <div
      className="group flex items-center gap-3 px-6 py-4 rounded-[6px] bg-white border border-[#E2E8F0] hover:border-[#030712] transition-all shrink-0 min-w-[260px]"
      title={`${name} — ${tag}`}
    >
      <div
        className="w-11 h-11 rounded-[6px] flex items-center justify-center font-display font-extrabold text-base shrink-0 transition-transform group-hover:scale-110"
        style={{ background: `${accent}14`, color: accent }}
      >
        {mono}
      </div>
      <div className="min-w-0">
        <div className="font-display font-bold text-sm text-[#0F1115] truncate">{name}</div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#4B5563] truncate">{tag}</div>
      </div>
    </div>
  );
}

function ClientsAndTestimonials() {
  // Duplicate the array so the marquee loops seamlessly.
  const loop = [...CLIENTS, ...CLIENTS];
  return (
    <section className="py-24 lg:py-32 border-b border-[#E2E8F0] bg-white overflow-hidden" data-testid="clients-testimonials-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <span className="text-xs font-mono text-[#4B5563]">// 07</span>
            <h2 className="font-display font-bold text-4xl md:text-5xl mt-3 leading-[1.05]">
              Loved by teams across <span className="bg-gradient-to-r from-[#0044FF] via-[#7C3AED] to-[#EC4899] bg-clip-text text-transparent">the UK and beyond</span>.
            </h2>
          </div>
          <p className="text-[#4B5563] max-w-md">
            From education consultancies to hosting providers, healthcare and EdTech networks — businesses are shipping HelloSir agents to real customers today.
          </p>
        </div>
      </div>

      {/* Marquee */}
      <div className="relative mt-14">
        {/* Fade edges */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-white to-transparent z-10" aria-hidden />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-white to-transparent z-10" aria-hidden />

        <div className="flex gap-4 animate-hs-marquee" style={{ width: "max-content" }}>
          {loop.map((c, i) => (
            <ClientLogo key={`${c.mono}-${i}`} {...c} />
          ))}
        </div>
      </div>

      {/* Testimonial grid */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-20 grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="testimonials-grid">
        {TESTIMONIALS.map((t) => (
          <article
            key={t.author}
            className="group relative rounded-[6px] bg-white border border-[#E2E8F0] p-7 hover:border-[#030712] transition-all hover:-translate-y-1 hover:shadow-[0_20px_40px_-12px_rgba(2,6,23,0.12)]"
            data-testid={`testimonial-${t.mono}`}
          >
            <div
              className="absolute inset-x-0 top-0 h-0.5 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"
              style={{ background: `linear-gradient(90deg, ${t.accent}, transparent)` }}
            />
            <Quote className="w-7 h-7 mb-4" style={{ color: t.accent }} />
            <p className="text-[#0F1115] text-base leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
            <div className="flex items-center gap-3 mt-6 pt-5 border-t border-[#F3F4F6]">
              <div
                className="w-11 h-11 rounded-[6px] flex items-center justify-center font-display font-extrabold text-sm shrink-0"
                style={{ background: `${t.accent}14`, color: t.accent }}
              >
                {t.mono}
              </div>
              <div className="min-w-0">
                <div className="font-display font-bold text-sm">{t.author}</div>
                <div className="text-[11px] font-mono text-[#4B5563] mt-0.5">{t.role}</div>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

export default function Landing() {
  return (
    <div data-testid="landing-page" className="min-h-screen bg-[#F9FAFB]">
      <Seo
        title="HelloSir.ai — AI Text Chatbot, Voice Chatbot & AI Calling Agent"
        description="HelloSir.ai is the all-in-one AI communication platform: text chatbot, voice chatbot and AI calling agent. 3× more conversations at up to 80% lower cost. 20+ languages, 1-click plugins for WordPress, Shopify, Magento, BigCommerce, Wix, Squarespace. A Cloud Lab Technologies (UK) product."
        keywords={[
          "AI chatbot", "AI voice chatbot", "AI calling agent", "AI customer service",
          "AI for business", "WordPress AI plugin", "Shopify AI assistant",
          "Magento chatbot", "BigCommerce AI", "Wix AI bot", "Squarespace chatbot",
          "multilingual AI chatbot", "AI receptionist", "AI agent platform",
          "best AI chatbot 2026", "AI for small business", "AI lead capture",
          "AI appointment booking", "AI sales agent", "Cloud Lab Technologies",
        ]}
        path="/"
        jsonLd={SITE_LD}
      />
      {/* NAV — shared header so it stays consistent across every marketing page */}
      <MarketingHeader />

      {/* HERO — warm orange/amber gradient blobs + floating channel props */}
      <section className="relative overflow-hidden border-b border-[#E2E8F0] bg-gradient-to-br from-[#FFF7ED] via-white to-[#FFEDD5]">
        {/* Animated gradient orbs */}
        <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full blur-3xl opacity-50 animate-hs-blob"
             style={{ background: "radial-gradient(circle, #FBBF24 0%, transparent 70%)" }} />
        <div className="absolute -bottom-32 right-0 w-[520px] h-[520px] rounded-full blur-3xl opacity-45 animate-hs-blob"
             style={{ background: "radial-gradient(circle, #F97316 0%, transparent 70%)", animationDelay: "3s" }} />
        <div className="absolute top-1/3 right-1/4 w-[360px] h-[360px] rounded-full blur-3xl opacity-35 animate-hs-blob"
             style={{ background: "radial-gradient(circle, #EA580C 0%, transparent 70%)", animationDelay: "6s" }} />

        {/* Floating channel props */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden>
          <div className="absolute top-[12%] left-[8%] animate-hs-float text-[#F97316] opacity-70" style={{ animationDelay: "0s" }}>
            <MessageSquare className="w-10 h-10" />
          </div>
          <div className="absolute top-[22%] right-[14%] animate-hs-float text-[#FBBF24] opacity-70" style={{ animationDelay: "2.5s" }}>
            <Mic className="w-8 h-8" />
          </div>
          <div className="absolute bottom-[28%] left-[16%] animate-hs-float text-[#EA580C] opacity-65" style={{ animationDelay: "5s" }}>
            <Phone className="w-9 h-9" />
          </div>
          <div className="absolute bottom-[18%] right-[8%] animate-hs-float text-[#F59E0B] opacity-65" style={{ animationDelay: "1s" }}>
            <Sparkles className="w-7 h-7" />
          </div>
          <div className="absolute top-[55%] left-[42%] animate-hs-float text-[#F97316] opacity-55" style={{ animationDelay: "3.5s" }}>
            <MessageSquare className="w-6 h-6" />
          </div>
          <div className="absolute top-[8%] left-[55%] animate-hs-float text-[#FBBF24] opacity-55" style={{ animationDelay: "4.2s" }}>
            <Mic className="w-7 h-7" />
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-36 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-xs font-mono bg-white/80 backdrop-blur border border-[#E2E8F0] px-3 py-1.5 rounded-sm mb-8 shadow-sm">
              <Sparkles className="w-3.5 h-3.5 text-[#F97316]" /> AI Communication Platform · v0.1
            </div>
            <h1 className="font-display font-extrabold text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.95] tracking-tight">
              <span className="bg-gradient-to-r from-[#FBBF24] via-[#F97316] to-[#EA580C] bg-clip-text text-transparent">3×</span> More Conversations.<br />
              Up to <span className="bg-gradient-to-r from-[#FBBF24] via-[#F97316] to-[#EA580C] bg-clip-text text-transparent">80%</span> Lower Cost.
            </h1>
            <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-3" data-testid="hero-channels">
              {[
                { label: "Text Chatbot", color: "#0044FF" },
                { label: "Voice Chatbot", color: "#10B981" },
                { label: "AI Calling Agent", color: "#7C3AED" },
              ].map((c) => (
                <li key={c.label} className="flex items-center gap-2 text-base md:text-lg font-medium text-[#0F1115]">
                  <span
                    className="inline-flex w-6 h-6 items-center justify-center rounded-full shrink-0"
                    style={{ background: `${c.color}18`, color: c.color }}
                  >
                    <Check className="w-3.5 h-3.5" strokeWidth={3} />
                  </span>
                  {c.label}
                </li>
              ))}
            </ul>
            <p className="font-display font-bold text-2xl md:text-3xl mt-8 text-[#0F1115]">
              Train once. <span className="text-[#4B5563] font-medium">Deploy everywhere.</span>
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-10">
              <Link to="/signup" className="btn-primary flex items-center gap-2" data-testid="hero-cta-start">
                Start free <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#products" className="btn-outline bg-white/80 backdrop-blur" data-testid="hero-cta-explore">Explore products</a>
            </div>
            <div className="flex items-center gap-6 mt-12 text-xs font-mono text-[#4B5563]">
              <span>Worldwide · multi-tenant · enterprise-grade</span>
            </div>
          </div>
          <div className="lg:col-span-5">
            <MultilingualVoiceDemo />
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section id="products" className="py-24 lg:py-32 border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <span className="text-xs font-mono text-[#4B5563]">// 01</span>
              <h2 className="font-display font-bold text-4xl md:text-5xl mt-3">Three channels.<br />One brain.</h2>
              <p className="text-[#4B5563] mt-4">Pick one or run all three. Knowledge, leads and history sync across channels automatically.</p>
            </div>
            <div className="lg:col-span-8 grid md:grid-cols-3 gap-6">
              {[
                { Icon: Phone, name: "Voice Calling Bot", desc: "Inbound + outbound on Twilio numbers via VAPI." },
                { Icon: MessageSquare, name: "Website Chat Bot", desc: "Embeddable widget trained on your KB." },
                { Icon: Mic, name: "Website Voice Bot", desc: "Real-time voice in the browser via OpenAI." },
              ].map(({ Icon, name, desc }) => (
                <div key={name} className="hs-card p-6">
                  <Icon className="w-7 h-7 text-[#0044FF]" />
                  <h3 className="font-display font-bold text-xl mt-6">{name}</h3>
                  <p className="text-sm text-[#4B5563] mt-2">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section id="industries" className="py-24 lg:py-32 border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <span className="text-xs font-mono text-[#4B5563]">// 02</span>
          <h2 className="font-display font-bold text-4xl md:text-5xl mt-3 max-w-2xl">Pre-trained for the work you do.</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-[#E2E8F0] mt-12 border border-[#E2E8F0]">
            {Industries.map(({ id, name, Icon, href }) => (
              <Link key={id} to={href} className="bg-white p-8 hover:bg-[#F3F4F6] transition-colors" data-testid={`industry-${id}`}>
                <Icon className="w-8 h-8 text-[#030712]" />
                <div className="font-display font-bold text-lg mt-6">{name}</div>
                <div className="text-xs font-mono text-[#0044FF] mt-2 opacity-0 group-hover:opacity-100">See use case →</div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/use-cases" className="text-sm text-[#0044FF] hover:underline font-medium" data-testid="all-use-cases-link">
              See all industry use cases →
            </Link>
          </div>
        </div>
      </section>

      {/* INTEGRATIONS — platforms + capabilities */}
      <PlatformIntegrations />

      {/* AGENTS CAN ___ — typewriter capability showcase */}
      <AgentsCanSection />

      {/* FEATURE STRIP — Knowledge in / Conversations out (animated visual) */}
      <section className="py-24 lg:py-32 border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <KnowledgeFeatureCard />
          </div>
          <div className="lg:col-span-6">
            <span className="text-xs font-mono text-[#4B5563]">// 05</span>
            <h2 className="font-display font-bold text-4xl md:text-5xl mt-3">Knowledge in.<br />Conversations out.</h2>
            <ul className="space-y-3 mt-8">
              {KNOWLEDGE_FEATURES.map((f) => (
                <li key={f.title} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#0044FF] mt-0.5" />
                  <span className="text-[#030712]">{f.title}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PRICING — Free trial + pay-as-you-go top-ups + interactive cost calculator */}
      <section id="pricing" className="py-24 lg:py-32 border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <span className="text-xs font-mono text-[#4B5563]">// 06</span>
          <h2 className="font-display font-bold text-4xl md:text-5xl mt-3">Pay only for what you use and the channel you prefer.</h2>
          <p className="text-[#4B5563] mt-3 max-w-2xl">Start free, then top up in $9.99 packs as you grow. No subscriptions. No surprise bills. Credits never expire.</p>

          {/* Free trial highlight + 4 top-up cards */}
          <div className="grid lg:grid-cols-5 gap-5 mt-12">
            {/* Free trial — spans 2 cols */}
            <div className="lg:col-span-2 p-8 border border-[#030712] rounded-[4px] bg-gradient-to-br from-[#0F1115] to-[#1E293B] text-white" data-testid="pricing-card-free">
              <div className="text-xs font-mono text-[#10B981]">free forever</div>
              <div className="font-display font-extrabold text-3xl mt-2">{FreeTrial.title}</div>
              <div className="font-display font-extrabold text-6xl mt-6">$0</div>
              <ul className="mt-6 space-y-2 text-sm">
                {FreeTrial.features.map((f) => (
                  <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-[#10B981] mt-0.5 shrink-0" /><span>{f}</span></li>
                ))}
              </ul>
              <Link to="/signup" className="block text-center mt-8 py-3 bg-[#10B981] hover:bg-[#0E9F6E] text-white text-sm font-medium" data-testid="free-cta">Start free — no card needed</Link>
            </div>

            {/* Top-up cards — span 3 cols, 1 column each */}
            <div className="lg:col-span-3 grid sm:grid-cols-2 gap-5">
              {TopUps.map((t) => (
                <div key={t.id} className={`hs-card p-6 relative ${t.popular ? "ring-2 ring-[#10B981]" : ""}`} data-testid={`pricing-card-${t.id}`}>
                  {t.popular && <div className="absolute -top-2 right-3 text-[10px] font-mono uppercase bg-[#10B981] text-white px-2 py-0.5">most loved</div>}
                  <div className="text-2xl">{t.icon}</div>
                  <div className="font-display font-bold text-lg mt-2">{t.name}</div>
                  <div className="font-display font-extrabold text-3xl mt-2">${t.price}</div>
                  <div className="text-xs font-mono mt-1" style={{ color: t.color }}>{t.unit}</div>
                  <div className="text-xs text-[#4B5563] mt-3">{t.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Interactive cost calculator */}
          <PricingCalculator />

          <div className="text-center mt-10 text-xs font-mono text-[#4B5563]">
            One paid top-up unlocks up to 5 agents. <Link to="/billing" className="text-[#0044FF] hover:underline">See full billing details →</Link>
          </div>
        </div>
      </section>

      {/* TRUSTED-BY + TESTIMONIALS */}
      <ClientsAndTestimonials />

      {/* AI VOICE CALLING — 4-step animated flow */}
      <VoiceCallingFlow />

      {/* FOOTER CTA */}
      <section className="py-20 lg:py-24 border-t border-[#E2E8F0]">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-display font-extrabold text-4xl md:text-5xl leading-[1.05]">
            Ship your first agent in <span className="bg-gradient-to-r from-[#FBBF24] via-[#F97316] to-[#EA580C] bg-clip-text text-transparent">10 minutes</span>.
          </h2>
          <p className="text-[#4B5563] mt-4 max-w-2xl mx-auto">
            100 free chats and 20 free voice minutes — no card required. Top up whenever you outgrow it.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/signup" className="btn-primary inline-flex items-center gap-2 text-base" data-testid="footer-cta-start">
              Start free — no card required <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/use-cases" className="btn-outline inline-flex items-center gap-2 text-base">
              See industry templates
            </Link>
          </div>
          <div className="text-xs font-mono text-[#4B5563] mt-6">Pay-as-you-go · no subscriptions · credits never expire</div>
        </div>
      </section>

      <MarketingFooter />

      <LandingChatBubble />
    </div>
  );
}
